import { useMemo } from 'react'
import { HoardOrder, SaleOrder } from '../../../../sales'
import { useEmployeeLoaders } from '../../../../administration'
import { WidgetCard } from '../widget-card/widgetCard'
import { WidgetList, WidgetListRow } from '../widget-list/widgetList'
import { formatAmount, getMonthRange, isInRange } from '../../../utils/dashboardUtils'
import { useOperationTotals } from '../../../hooks/useOperationTotals'

type Props = {
    loading: boolean
    error: boolean
    sales: SaleOrder[]
    hoards: HoardOrder[]
}

// Sellers ordered by what they sold this month (sales + hoards)
export const SellerRankingWidget = (props: Props) => {
    const { loading, error, sales, hoards } = props

    const { getSaleTotal, getHoardTotal } = useOperationTotals()
    const employeeLoaders = useEmployeeLoaders()

    const rows = useMemo((): WidgetListRow[] => {
        const range = getMonthRange(0)
        const totals = new Map<string, { amount: number, count: number }>()
        const add = (sellerId: string, amount: number) => {
            const current = totals.get(sellerId) || { amount: 0, count: 0 }
            totals.set(sellerId, { amount: current.amount + amount, count: current.count + 1 })
        }
        sales.filter(sale => isInRange(sale.creationDate, range)).forEach(sale => add(sale.sellerId, getSaleTotal(sale)))
        hoards.filter(hoard => isInRange(hoard.creationDate, range)).forEach(hoard => add(hoard.sellerId, getHoardTotal(hoard)))

        const { findEmployee } = employeeLoaders()
        return Array.from(totals.entries())
            .sort(([, a], [, b]) => b.amount - a.amount)
            .map(([sellerId, { amount, count }]) => ({
                key: sellerId,
                label: findEmployee(sellerId)?.name || 'Sin vendedor',
                detail: `${count} op.`,
                value: formatAmount(amount)
            }))
    }, [sales, hoards])

    return (
        <WidgetCard
            title="Ranking de vendedores del mes"
            loading={loading}
            error={error}
            empty={!loading && !error && rows.length === 0}
            emptyLabel="Todavía no hay ventas este mes"
        >
            <WidgetList rows={rows} ranked />
        </WidgetCard>
    )
}
